'use client'

import { useEffect, useState } from 'react'
import { MdInfoOutline, MdReplay, MdWarning } from 'react-icons/md'
import {
    currencyFormat,
    formatCentsToDisplay,
    parseCurrencyToCents,
} from '@/lib/form-state/money'
import type { RefundMode } from '@/lib/api/financial/billing-refund-api'
import { useInstantRefundPolicy } from '@/lib/hooks/use-instant-refund-policy'

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/**
 * O que o modal devolve — e os dois caminhos são MESMO diferentes, não um com
 * campos opcionais: na fila o estorno é um pedido (valor e motivo, decidido
 * depois); no imediato é a devolução inteira (modo, instituição e senha), feita
 * agora. O tipo discriminado por `instant` obriga quem chama a tratar os dois.
 */
export type RefundSubmitData =
    | { instant: false; amount: number; reason: string }
    | {
          instant: true
          amount: number
          reason: string
          password: string
          mode: RefundMode
          refundedVia?: string
      }

interface RequestRefundModalProps {
    isOpen: boolean
    onClose: () => void
    /** `amount` em centavos. */
    onConfirm: (data: RefundSubmitData) => Promise<void>
    loading?: boolean
    billing: {
        id: number
        amount: number
        paidAmount?: number | null
        refundedAmount?: number | null
        /**
         * Pagamento ENTROU pela conta digital — só aí existe o que estornar no
         * provedor. Sem ele, o dinheiro volta por fora e aqui só se registra.
         */
        providerPaymentId?: string | null
    }
}

/**
 * Estorno a partir da cobrança.
 *
 * Nem toda cobrança nasce de um agendamento — pedido da loja, mensalidade, lançamento
 * avulso —, então o estorno não pode existir só como efeito colateral do cancelamento de
 * um exame. Aqui a porta é a própria cobrança.
 *
 * **Dois fluxos, um modal.** Com "Estorno sem aprovação" DESLIGADO (o padrão), abre
 * solicitação e um operador financeiro aprova com senha. LIGADO, a devolução acontece
 * aqui: o modal passa a perguntar também por onde o dinheiro volta e a senha de quem
 * está estornando — o que a fila perguntaria ao aprovador. Separar em dois modais
 * duplicaria valor, motivo, saldo devolvível e o aviso do repasse.
 *
 * O valor vem preenchido com o saldo devolvível inteiro (o caso comum) e pode ser
 * reduzido para reter uma multa de cancelamento.
 */
export default function RequestRefundModal({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    billing,
}: RequestRefundModalProps) {
    const paid = billing.paidAmount ?? billing.amount
    const alreadyRefunded = billing.refundedAmount ?? 0
    const refundable = Math.max(0, paid - alreadyRefunded)

    const { allowInstantRefund } = useInstantRefundPolicy()

    // String mascarada "R$ 12,34" — a máscara de moeda do projeto. A conversão para
    // centavos é feita na confirmação (e para validar).
    const [amount, setAmount] = useState('')
    const [reason, setReason] = useState('')
    const [password, setPassword] = useState('')
    const [refundedVia, setRefundedVia] = useState('')

    /**
     * Estorno pela conta digital só existe quando o pagamento entrou por lá; nos
     * demais (espécie, maquininha, baixa manual) não há o que estornar no provedor,
     * e o operador devolve por fora e registra aqui.
     */
    const canRefundViaBaas = !!billing.providerPaymentId
    const [mode, setMode] = useState<RefundMode>('BAAS')

    useEffect(() => {
        if (isOpen) {
            setAmount(formatCentsToDisplay(refundable))
            setReason('')
            setPassword('')
            setRefundedVia('')
            setMode(canRefundViaBaas ? 'BAAS' : 'ALREADY_REFUNDED')
        }
    }, [isOpen, refundable, canRefundViaBaas])

    if (!isOpen) return null

    const amountCents = parseCurrencyToCents(amount)
    const amountInvalid =
        !Number.isFinite(amountCents) ||
        amountCents <= 0 ||
        amountCents > refundable
    const isPartial =
        Number.isFinite(amountCents) &&
        amountCents > 0 &&
        amountCents < refundable

    // No estorno imediato, o que a fila pediria ao aprovador é pedido aqui: a
    // instituição (quando o dinheiro voltou por fora) e a senha.
    const instantFieldsMissing =
        allowInstantRefund &&
        (!password || (mode === 'ALREADY_REFUNDED' && !refundedVia.trim()))

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <MdReplay className="text-info text-xl" />
                    {allowInstantRefund
                        ? 'Estornar cobrança'
                        : 'Solicitar estorno'}
                </h3>

                <p className="text-sm text-base-content/70 mt-2">
                    {allowInstantRefund
                        ? 'O estorno é feito agora, sem passar pela fila de aprovação'
                        : 'A solicitação será enviada para aprovação de um operador financeiro'}
                </p>

                <div className="bg-base-200/50 rounded-xl p-3 mt-4 text-sm space-y-1">
                    <div className="flex justify-between">
                        <span className="text-base-content/60">Valor pago</span>
                        <span className="font-medium">
                            {formatCurrency(paid)}
                        </span>
                    </div>
                    {alreadyRefunded > 0 && (
                        <div className="flex justify-between">
                            <span className="text-base-content/60">
                                Já estornado
                            </span>
                            <span className="font-medium text-info">
                                {formatCurrency(alreadyRefunded)}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between border-t border-base-300/60 pt-1">
                        <span className="text-base-content/60">
                            Disponível para estorno
                        </span>
                        <span className="font-bold">
                            {formatCurrency(refundable)}
                        </span>
                    </div>
                </div>

                <div className="form-control w-full mt-5">
                    <label className="label pb-2" htmlFor="refund-amount">
                        <span className="label-text font-medium">
                            Valor a estornar{' '}
                            <span className="text-error">*</span>
                        </span>
                    </label>
                    <input
                        id="refund-amount"
                        type="text"
                        inputMode="numeric"
                        placeholder="R$ 0,00"
                        className="input input-bordered w-full"
                        value={amount}
                        onChange={e =>
                            setAmount(currencyFormat(e.target.value, true))
                        }
                        disabled={loading}
                    />
                    <p className="text-xs text-base-content/60 pt-2">
                        {isPartial
                            ? 'Estorno parcial: o restante fica com a empresa (multa, taxa de cancelamento).'
                            : 'Devolução integral do que o cliente pagou.'}
                    </p>
                </div>

                <div className="form-control w-full mt-2">
                    <label className="label pb-2" htmlFor="refund-reason">
                        <span className="label-text font-medium">
                            Motivo do estorno{' '}
                            <span className="text-error">*</span>
                        </span>
                    </label>
                    <textarea
                        id="refund-reason"
                        className="textarea textarea-bordered w-full h-24 resize-none"
                        placeholder="Ex.: cobrança em duplicidade, serviço não prestado, desistência do cliente..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        maxLength={500}
                        disabled={loading}
                    />
                    <label className="label pt-2">
                        <span className="label-text-alt text-base-content/60">
                            {allowInstantRefund
                                ? 'Fica registrado na cobrança e no recibo do estorno.'
                                : 'O operador financeiro lerá este motivo antes de aprovar.'}
                        </span>
                        <span className="label-text-alt text-base-content/40">
                            {reason.length}/500
                        </span>
                    </label>
                </div>

                {/* Estorno imediato: o modal assume o papel da aprovação e pergunta
                    o que ela perguntaria — por onde o dinheiro volta e a senha. */}
                {allowInstantRefund && (
                    <>
                        <div className="form-control w-full mt-4">
                            <label className="label pb-2">
                                <span className="label-text font-medium">
                                    Por onde o dinheiro volta{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <div className="flex flex-col gap-2">
                                {/* A conta digital só aparece quando o pagamento
                                    entrou por ela: no resto (espécie, maquininha,
                                    baixa manual) não há o que estornar no provedor. */}
                                {canRefundViaBaas && (
                                    <label
                                        className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${
                                            mode === 'BAAS'
                                                ? 'border-info bg-info/5'
                                                : 'border-base-300 hover:bg-base-200/40'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="instant-refund-mode"
                                            className="radio radio-info radio-sm mt-0.5"
                                            checked={mode === 'BAAS'}
                                            onChange={() => setMode('BAAS')}
                                            disabled={loading}
                                        />
                                        <span>
                                            <span className="block text-sm font-medium">
                                                Estornar pela conta digital
                                            </span>
                                            <span className="block text-xs text-base-content/60">
                                                O valor volta ao cliente pelo
                                                provedor, na cobrança original.
                                            </span>
                                        </span>
                                    </label>
                                )}
                                <label
                                    className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${
                                        mode === 'ALREADY_REFUNDED'
                                            ? 'border-info bg-info/5'
                                            : 'border-base-300 hover:bg-base-200/40'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="instant-refund-mode"
                                        className="radio radio-info radio-sm mt-0.5"
                                        checked={mode === 'ALREADY_REFUNDED'}
                                        onChange={() =>
                                            setMode('ALREADY_REFUNDED')
                                        }
                                        disabled={loading}
                                    />
                                    <span>
                                        <span className="block text-sm font-medium">
                                            Já foi devolvido por fora
                                        </span>
                                        <span className="block text-xs text-base-content/60">
                                            Dinheiro em espécie, outro banco. Só
                                            registra a devolução, sem mexer na
                                            conta digital.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {mode === 'ALREADY_REFUNDED' && (
                            <div className="form-control w-full mt-3">
                                <label
                                    className="label pb-2"
                                    htmlFor="refund-via"
                                >
                                    <span className="label-text font-medium">
                                        Como foi devolvido{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                {/* O campo de senha logo abaixo faz o Chrome tratar
                                    este bloco como um formulário de login e despejar
                                    aqui o e-mail salvo — foi o que aconteceu no
                                    primeiro teste. `new-password` na senha é o que
                                    corta o casamento; o `off` daqui completa. */}
                                <input
                                    id="refund-via"
                                    name="refund-via"
                                    type="text"
                                    autoComplete="off"
                                    className="input input-bordered w-full"
                                    placeholder="Ex.: dinheiro em espécie, Banco do Brasil, PIX pessoal"
                                    value={refundedVia}
                                    onChange={e =>
                                        setRefundedVia(e.target.value)
                                    }
                                    maxLength={120}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        <div className="form-control w-full mt-3">
                            <label
                                className="label pb-2"
                                htmlFor="refund-password"
                            >
                                <span className="label-text font-medium">
                                    Sua senha{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                id="refund-password"
                                name="refund-password"
                                type="password"
                                // Não é o login: sem isto o Chrome preenche a senha
                                // salva e, de quebra, procura um campo de usuário
                                // para preencher com o e-mail.
                                autoComplete="new-password"
                                className="input input-bordered w-full"
                                placeholder="Confirme com a sua senha"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                disabled={loading}
                            />
                            <p className="text-xs text-base-content/60 pt-2">
                                Dinheiro saindo é confirmado com senha, aqui
                                como na fila de aprovação.
                            </p>
                        </div>
                    </>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        className="btn btn-info"
                        disabled={
                            loading ||
                            amountInvalid ||
                            !reason.trim() ||
                            instantFieldsMissing
                        }
                        onClick={() =>
                            onConfirm(
                                allowInstantRefund
                                    ? {
                                          instant: true,
                                          amount: amountCents,
                                          reason: reason.trim(),
                                          password,
                                          mode,
                                          refundedVia:
                                              mode === 'ALREADY_REFUNDED'
                                                  ? refundedVia.trim()
                                                  : undefined,
                                      }
                                    : {
                                          instant: false,
                                          amount: amountCents,
                                          reason: reason.trim(),
                                      }
                            )
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdReplay className="text-base" />
                        )}
                        {allowInstantRefund
                            ? mode === 'BAAS'
                                ? 'Estornar agora'
                                : 'Registrar estorno'
                            : 'Solicitar estorno'}
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose} disabled={loading}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
